/**
 * 브라우저 저장소 관리 유틸리티 (쿠키 우선, localStorage 대체)
 */
import {
  AUTH_COOKIES,
  clearAuthCookies,
  getCookie,
  getAuthToken as getCookieAuthToken,
  removeCookie,
  setCookie,
  setAuthToken as setCookieAuthToken,
} from './cookie';
import { extractUserFromJWT, isValidJWT } from './jwt';

// 저장소 타입
type StorageType = 'cookie' | 'localStorage' | 'sessionStorage';

// 현재 사용 중인 저장소 타입을 추적 (키별로 관리)
let currentStorageType: StorageType = 'cookie';
const storageTypeByKey: Map<string, StorageType> = new Map();

/**
 * 저장소 지원 여부 확인
 */
const isStorageSupported = (type: StorageType): boolean => {
  try {
    switch (type) {
      case 'cookie': {
        // 간단한 쿠키 테스트
        document.cookie = 'test=1';
        const supported = document.cookie.includes('test=1');
        document.cookie = 'test=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        return supported;
      }
      case 'localStorage': {
        localStorage.setItem('test', '1');
        const result = localStorage.getItem('test') === '1';
        localStorage.removeItem('test');
        return result;
      }
      case 'sessionStorage': {
        sessionStorage.setItem('test', '1');
        const sessionResult = sessionStorage.getItem('test') === '1';
        sessionStorage.removeItem('test');
        return sessionResult;
      }
      default:
        return false;
    }
  } catch (error) {
    console.warn(`${type} 지원하지 않음:`, error);
    return false;
  }
};

/**
 * 사용할 저장소 타입 결정
 */
const determineStorageType = (): StorageType => {
  if (isStorageSupported('cookie')) {
    console.log('[determineStorageType] 쿠키 사용 가능');
    return 'cookie';
  } else if (isStorageSupported('localStorage')) {
    console.log('[determineStorageType] 쿠키 사용 불가, localStorage 사용');
    return 'localStorage';
  } else if (isStorageSupported('sessionStorage')) {
    console.log(
      '[determineStorageType] 쿠키와 localStorage 사용 불가, sessionStorage 사용'
    );
    return 'sessionStorage';
  } else {
    console.error('[determineStorageType] 모든 저장소 사용 불가');
    return 'localStorage'; // 최후의 수단
  }
};

// 초기화 시 저장소 타입 결정
currentStorageType = determineStorageType();

/**
 * 통합 저장소 설정
 */
export const setItem = (
  key: string,
  value: string,
  options?: { expires?: number }
): void => {
  console.log(
    `[setItem] [${currentStorageType}] 저장:`,
    key,
    value.substring(0, 50) + '...'
  );

  // 인증 토큰은 절대 이 함수를 통하지 않고 직접 쿠키 함수 호출
  if (key === AUTH_COOKIES.ACCESS_TOKEN) {
    console.log('[setItem] 인증 토큰은 setItem을 우회하여 직접 쿠키에 저장');
    setCookieAuthToken(value);
    storageTypeByKey.set(key, 'cookie');
    return;
  }

  // 다른 데이터는 기존 로직
  let targetStorageType = currentStorageType;
  const estimatedSize =
    encodeURIComponent(key).length + encodeURIComponent(value).length + 100;
  if (targetStorageType === 'cookie' && estimatedSize > 4000) {
    console.warn(
      `[setItem] [${key}] 쿠키 크기 제한 초과 (${estimatedSize} bytes), localStorage로 대체`
    );
    targetStorageType = 'localStorage';
  }

  try {
    switch (targetStorageType) {
      case 'cookie':
        // 일반 데이터만 이 경로로 저장 (토큰은 이미 위에서 처리됨)
        setCookie(key, value, {
          expires: options?.expires || 7,
          secure: false,
          sameSite: 'lax',
        });

        // 쿠키 설정 후 실제로 저장되었는지 확인
        setTimeout(() => {
          const saved = getCookie(key);
          if (!saved && value) {
            console.warn(
              `[setItem] [${key}] 쿠키 저장 실패, localStorage로 대체`
            );
            storageTypeByKey.set(key, 'localStorage');
            // 재귀 호출로 localStorage에 저장
            const data = {
              value,
              expires: options?.expires
                ? Date.now() + options.expires * 24 * 60 * 60 * 1000
                : null,
            };
            localStorage.setItem(key, JSON.stringify(data));
            console.log(`[setItem] [${key}] localStorage에 대체 저장 완료`);
          } else {
            storageTypeByKey.set(key, 'cookie');
            console.log(`[setItem] [${key}] 쿠키에 저장 완료`);
          }
        }, 100);
        break;

      case 'localStorage': {
        const data = {
          value,
          expires: options?.expires
            ? Date.now() + options.expires * 24 * 60 * 60 * 1000
            : null,
        };
        localStorage.setItem(key, JSON.stringify(data));
        storageTypeByKey.set(key, 'localStorage');
        console.log(`[setItem] [${key}] localStorage에 저장 완료`);
        break;
      }

      case 'sessionStorage':
        sessionStorage.setItem(key, value);
        storageTypeByKey.set(key, 'sessionStorage');
        console.log(`[setItem] [${key}] sessionStorage에 저장 완료`);
        break;

      default:
        break;
    }
  } catch (error) {
    console.error(`[setItem] [${targetStorageType}] 저장 실패:`, error);
    // 현재 저장소가 실패하면 localStorage로 대체
    if (targetStorageType !== 'localStorage') {
      console.log('[setItem] localStorage로 대체 시도');
      try {
        const data = {
          value,
          expires: options?.expires
            ? Date.now() + options.expires * 24 * 60 * 60 * 1000
            : null,
        };
        localStorage.setItem(key, JSON.stringify(data));
        storageTypeByKey.set(key, 'localStorage');
        console.log(`[setItem] [${key}] localStorage에 대체 저장 완료`);
      } catch (localError) {
        console.error(`[setItem] localStorage 대체 저장도 실패:`, localError);
      }
    }
  }
};

/**
 * 통합 저장소 조회 (여러 저장소에서 확인)
 */
export const getItem = (key: string): string | null => {
  // 1. 키별 저장소 타입이 기록되어 있다면 해당 저장소에서 먼저 시도
  const keyStorageType = storageTypeByKey.get(key);
  if (keyStorageType) {
    try {
      let result = null;

      switch (keyStorageType) {
        case 'cookie':
          if (key === AUTH_COOKIES.ACCESS_TOKEN) {
            // 인증 토큰은 특별한 분할 쿠키 함수 사용
            result = getCookieAuthToken();
          } else {
            result = getCookie(key);
          }
          break;

        case 'localStorage': {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const data = JSON.parse(stored);
              if (data.expires && data.expires < Date.now()) {
                localStorage.removeItem(key);
                storageTypeByKey.delete(key);
                return null;
              }
              result = data.value;
            } catch {
              result = stored;
            }
          }
          break;
        }

        case 'sessionStorage':
          result = sessionStorage.getItem(key);
          break;
        default:
          break;
      }

      if (result) {
        return result;
      }
    } catch (error) {
      console.warn(
        `[getItem] 키별 저장소 조회 실패 [${keyStorageType}]:`,
        error
      );
    }
  }

  // 2. 키별 저장소에서 찾지 못했거나 기록이 없는 경우 모든 저장소에서 검색
  try {
    let result = null;

    switch (currentStorageType) {
      case 'cookie':
        if (key === AUTH_COOKIES.ACCESS_TOKEN) {
          // 인증 토큰은 특별한 분할 쿠키 함수 사용
          result = getCookieAuthToken();
        } else {
          result = getCookie(key);
        }
        break;

      case 'localStorage': {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const data = JSON.parse(stored);
            // 만료 확인
            if (data.expires && data.expires < Date.now()) {
              localStorage.removeItem(key);
              return null;
            }
            result = data.value;
          } catch {
            // 이전 형식의 데이터일 경우 그대로 반환
            result = stored;
          }
        }
        break;
      }

      case 'sessionStorage':
        result = sessionStorage.getItem(key);
        break;
      default:
        break;
    }

    // 3. 현재 저장소에서 찾지 못했을 경우 다른 저장소들에서 검색
    if (!result) {
      // 쿠키에서 검색
      if (currentStorageType !== 'cookie' && keyStorageType !== 'cookie') {
        let cookieValue = null;
        if (key === AUTH_COOKIES.ACCESS_TOKEN) {
          // 인증 토큰은 특별한 분할 쿠키 함수 사용
          cookieValue = getCookieAuthToken();
        } else {
          cookieValue = getCookie(key);
        }

        if (cookieValue) {
          console.log(`[getItem] [${key}] 쿠키에서 발견`);
          storageTypeByKey.set(key, 'cookie'); // 발견된 위치 기록
          return cookieValue;
        }
      }

      // localStorage에서 검색
      if (
        currentStorageType !== 'localStorage' &&
        keyStorageType !== 'localStorage'
      ) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const data = JSON.parse(stored);
              if (data.expires && data.expires < Date.now()) {
                localStorage.removeItem(key);
                return null;
              }
              console.log(`[getItem] [${key}] localStorage에서 발견`);
              storageTypeByKey.set(key, 'localStorage'); // 발견된 위치 기록
              return data.value;
            } catch {
              console.log(`[getItem] [${key}] localStorage에서 발견 (원시값)`);
              storageTypeByKey.set(key, 'localStorage'); // 발견된 위치 기록
              return stored;
            }
          }
        } catch (error) {
          console.warn(`[getItem] localStorage 접근 실패:`, error);
        }
      }

      // sessionStorage에서 검색
      if (
        currentStorageType !== 'sessionStorage' &&
        keyStorageType !== 'sessionStorage'
      ) {
        try {
          const sessionValue = sessionStorage.getItem(key);
          if (sessionValue) {
            console.log(`[getItem] [${key}] sessionStorage에서 발견`);
            storageTypeByKey.set(key, 'sessionStorage'); // 발견된 위치 기록
            return sessionValue;
          }
        } catch (error) {
          console.warn(`[getItem] sessionStorage 접근 실패:`, error);
        }
      }
    }

    return result;
  } catch (error) {
    console.error(`[getItem] [${currentStorageType}] 조회 실패:`, error);
    return null;
  }
};

/**
 * 통합 저장소 삭제 (모든 저장소에서 삭제)
 */
export const removeItem = (key: string): void => {
  console.log(`[removeItem] 모든 저장소에서 삭제:`, key);

  // 모든 저장소에서 삭제 시도
  try {
    removeCookie(key);
  } catch (error) {
    console.warn(`[removeItem] 쿠키 삭제 실패:`, error);
  }

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`[removeItem] localStorage 삭제 실패:`, error);
  }

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(`[removeItem] sessionStorage 삭제 실패:`, error);
  }

  // 키별 저장소 타입 기록도 삭제
  storageTypeByKey.delete(key);

  console.log(`[removeItem] [${key}] 모든 저장소에서 삭제 완료`);
};

/**
 * 인증 토큰 설정 (강제로 쿠키에 저장)
 */
export const setAuthToken = (token: string): void => {
  console.log(
    '[setAuthToken] 인증 토큰 쿠키 저장 시작:',
    token ? '***' + token.slice(-10) : null
  );

  // 쿠키 전용 함수 직접 호출 (storage.ts의 setItem 우회)
  setCookieAuthToken(token);

  // 키별 저장소 타입 기록
  storageTypeByKey.set(AUTH_COOKIES.ACCESS_TOKEN, 'cookie');

  console.log('[setAuthToken] 인증 토큰 쿠키 저장 완료');
};

/**
 * 인증 토큰 조회 (쿠키에서 직접 조회)
 */
export const getAuthToken = (): string | null => {
  // 쿠키 전용 함수 직접 호출 (storage.ts의 getItem 우회)
  return getCookieAuthToken();
};

/**
 * 사용자 정보 설정 (JWT 기반으로 더 이상 별도 저장하지 않음)
 */
export const setUserInfo = (user: any): void => {
  // JWT에서 모든 정보를 얻을 수 있으므로 별도 저장하지 않음
  console.log('[setUserInfo] 사용자 정보는 JWT에서 자동으로 추출됩니다');
};

/**
 * 사용자 정보 조회 (JWT에서 추출)
 */
export const getUserInfo = (): any | null => {
  const token = getAuthToken();
  if (!token) {
    console.log('[getUserInfo] 토큰이 없어서 사용자 정보를 가져올 수 없습니다');
    return null;
  }

  // JWT에서 사용자 정보 추출
  try {
    return extractUserFromJWT(token);
  } catch (error) {
    console.error('[getUserInfo] JWT 사용자 정보 추출 실패:', error);
    return null;
  }
};

/**
 * 인증 정보 모두 삭제 (쿠키에서 직접 삭제)
 */
export const clearAuthData = (): void => {
  console.log('[clearAuthData] 인증 토큰 쿠키 삭제 시작');

  // 쿠키 전용 함수 직접 호출
  clearAuthCookies();

  // 키별 저장소 타입 기록 삭제
  storageTypeByKey.delete(AUTH_COOKIES.ACCESS_TOKEN);

  console.log('[clearAuthData] 인증 토큰 쿠키 삭제 완료');
};

/**
 * 현재 저장소 타입 조회
 */
export const getCurrentStorageType = (): StorageType => {
  return currentStorageType;
};

/**
 * 저장소 상태 디버깅
 */
export const debugStorage = (): void => {
  const token = getAuthToken();
  const user = getUserInfo();

  console.log('[debugStorage] 저장소 상태:', {
    현재저장소: currentStorageType,
    키별저장소: Object.fromEntries(storageTypeByKey),
    토큰: token ? '***있음' : '없음',
    토큰길이: token?.length || 0,
    JWT유효성: token ? isValidJWT(token) : false,
    사용자정보: user ? `${user.email} (JWT에서 추출)` : '없음',
    쿠키지원: isStorageSupported('cookie'),
    로컬스토리지지원: isStorageSupported('localStorage'),
    세션스토리지지원: isStorageSupported('sessionStorage'),
    실제저장위치: {
      쿠키: document.cookie ? '데이터있음' : '비어있음',
      로컬스토리지: localStorage.length
        ? `${localStorage.length}개 항목`
        : '비어있음',
      세션스토리지: sessionStorage.length
        ? `${sessionStorage.length}개 항목`
        : '비어있음',
    },
  });
};

/**
 * 기존 localStorage의 토큰을 쿠키로 마이그레이션
 */
export const migrateTokenToCookie = (): void => {
  // localStorage에서 기존 토큰 확인
  const legacyKeys = ['token', 'accessToken'];
  let foundToken = null;

  legacyKeys.some((key) => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        // JSON 형태인지 확인
        try {
          const parsed = JSON.parse(stored);
          if (parsed.value && typeof parsed.value === 'string') {
            foundToken = parsed.value;
            return true;
          }
        } catch {
          // 직접 문자열로 저장된 경우
          if (typeof stored === 'string' && stored.length > 100) {
            foundToken = stored;
            return true;
          }
        }
      }
    } catch (_error) {
      return false;
    }
    return false; // 계속 진행
  });

  if (foundToken) {
    // 쿠키로 마이그레이션
    setAuthToken(foundToken);

    // 기존 localStorage 토큰들 삭제
    legacyKeys.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (_error) {
        return false;
      }
    });
  } else {
    console.log('[migrateTokenToCookie] 마이그레이션할 토큰 없음');
  }
};

// 전역 함수로 노출 (개발 환경)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).debugStorage = debugStorage;
  (window as any).migrateTokenToCookie = migrateTokenToCookie;
  (window as any).testStorage = () => {
    console.log('[testStorage] 저장소 테스트 시작');
    setAuthToken('test-token-12345');
    setUserInfo({ _id: '123', email: 'test@test.com', name: 'Test User' });

    setTimeout(() => {
      console.log('[testStorage] 저장소 테스트 결과:', {
        토큰: getAuthToken(),
        사용자정보: getUserInfo(),
      });
      clearAuthData();
    }, 100);
  };

  // 페이지 로드 시 자동 마이그레이션
  setTimeout(() => {
    migrateTokenToCookie();
  }, 1000);
}
